// ============================================================================
// Formats RESULTATS FINANCIERS (E2) — helpers locale-aware (FR+EN des l'ecriture)
// ============================================================================
// Meme raison que pages/accueil/format.ts : les helpers historiques
// src/lib/format/currency.ts sont fr-CA FIGES (heritage PoC, consommes par les
// comites) — l'ecran E2 est bilingue natif, chaque format prend la locale i18n
// courante. Module par ecran = pattern etabli du parc (accueil/format.ts,
// affichages/format.ts, comites/dates.ts). Fonctions pures -> testables.
//
// Recouvrement CONNU avec pages/accueil/format.ts (formatCad / formatMonthLong) :
// consolidation a faire quand un chore touchera l'accueil — E2 n'a pas le droit
// d'editer les fichiers des ecrans livres (merges seriaises par le cerveau).

const localeTag = (locale: string): string => (locale.startsWith('en') ? 'en-CA' : 'fr-CA');

/** Montant CAD entier (« 2 345 782 $ » / « $2,345,782 »). */
export function formatCad(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Montant CAD TOUJOURS signe (« +12 500 $ », « -3 200 $ ») — ecarts uniquement. */
export function formatSignedCad(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  }).format(value);
}

/** Ratio (0.032) → « +3,2 % » (signe force) — reserve aux ECARTS. « — » si non fini. */
export function formatVariancePct(ratio: number, locale: string): string {
  if (!Number.isFinite(ratio)) return '—';
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(ratio);
}

/** Ratio (0.152) → « 15,2 % » SANS signe — pour les NIVEAUX (marge brute, parts). */
export function formatLevelPct(ratio: number, locale: string): string {
  if (!Number.isFinite(ratio)) return '—';
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(ratio);
}

/**
 * Ratio → « 12,8 % » SANS signe ni decimale forcee — pour les deltas de
 * GsKpiComparison, dont le sens est deja porte par le chevron ▲/▼ (parite E1a).
 */
export function formatDeltaPct(ratio: number, locale: string): string {
  if (!Number.isFinite(ratio)) return '—';
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(ratio));
}

/** Periode 'YYYY-MM' → « avril 2026 » / « April 2026 » (UTC — pas de recul de fuseau). */
export function formatMonthLong(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return new Intl.DateTimeFormat(localeTag(locale), {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

/** Periode 'YYYY-MM' → « avr. » / « Apr » — etiquettes d'axe des graphiques. */
export function formatMonthShort(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return new Intl.DateTimeFormat(localeTag(locale), {
    month: 'short', timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

/** Horodatage « derniere mise a jour » du pied de page detail (date + heure courtes). */
export function formatStamp(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(localeTag(locale), {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(d);
}

/**
 * Classe de couleur d'une variance DEJA normalisee « favorable = positif ».
 *
 * N'UTILISE PAS `varianceClass` de la lib portee : celle-ci rend
 * `text-emerald-700` / `text-[var(--gs-red)]` — un cran `emerald` absent de la
 * palette du parc, et une variable `--gs-red` qui n'existe PAS dans gest (elle
 * vient du globals.css du PoC). Elle sortirait une couleur invalide et violerait
 * la règle #28 (classe arbitraire, DS-012). On reste sur l'échelle nommée du
 * parc, alignee sur GsKpiComparison (vert = favorable, rouge = defavorable).
 * La lib reste intacte et testee — seul son helper de CLASSES est ecarte.
 */
export function varianceToneClass(variance: number): string {
  if (!Number.isFinite(variance) || variance === 0) return 'text-slate-400 dark:text-slate-500';
  return variance > 0
    ? 'text-green-700 dark:text-green-500'
    : 'text-red-600 dark:text-red-400';
}
